import styled from '@emotion/styled'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { useState, useMemo } from 'react'
import { Activity } from '../../../pure-js/types/GrooverTypes'
import printIcon from '../../public/printIcon.svg'
import { EIGHT_PX, TWELVE_PX } from '../enums/Spacings'
import { State } from '../hooks/useAppState'
import { useClient } from '../hooks/useClient'
import { isGetObfuscatedEnrollmentResponse, useEnrollment } from '../libs/ReactQueryHooks'
import Box from './Box'
import CourseRegistrationReceipt from './CourseRegistrationReceipt'
import CourseRegistrationReceiptSsn from './CourseRegistrationReceiptSsn'
import GrooverText from './GrooverText'
import penIcon from '/public/penIcon.svg'
import plusIcon from '/public/plusIcon.svg'

type Props = {
  state: State
  activity: Activity
  onSetMainUserSsn: (mainUserSsn: string) => void
  onSetPartnerUserSsn: (partnerUserSsn: string) => void
}

const CourseRegistrationReceiptWrapper = ({ state, activity, onSetMainUserSsn, onSetPartnerUserSsn }: Props) => {
  const client = useClient(activity)
  const enrollmentUserIdFromUrl = useMemo(
    () => new URLSearchParams(window.location.search).get('u')?.trim() || undefined,
    []
  )
  const { data: obfuscatedEnrollmentResponse } = useEnrollment(state, enrollmentUserIdFromUrl)
  const enrollment = isGetObfuscatedEnrollmentResponse(obfuscatedEnrollmentResponse)
    ? obfuscatedEnrollmentResponse.enrollment
    : undefined
  const [showPersonalNumberInput, setShowPersonalNumberInput] = useState(false)
  const [userMainSsn, setUserMainSsn] = useState<string | undefined>(undefined)
  const [userPartnerSsn, setUserPartnerSsn] = useState<string | undefined>(undefined)

  // Determine user types based on enrollment data
  const userType: ('mainUser' | 'partnerUser')[] = useMemo(() => {
    const types: ('mainUser' | 'partnerUser')[] = []
    const e = enrollment || state.obfuscatedEnrollment

    if (e.mainUser?.id) {
      types.push('mainUser')
    }
    if (e.partnerUser?.id) {
      types.push('partnerUser')
    }
    return types
  }, [enrollment, state.obfuscatedEnrollment])

  const handleDownloadPDF = async (index: number) => {
    const element = document.getElementById(`receipt-${index}`)
    if (!element) return

    const canvas = await html2canvas(element, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true
    })
    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    const imgWidth = 190 // A4 width in mm minus margins
    const pageHeight = 277 // A4 height in mm minus margins (297 - 20)
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    let heightLeft = imgHeight
    let position = 10

    // Add first page
    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    // Add additional pages if content exceeds one page
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    pdf.save(`${client?.name}_Course_Registration_Receipt.pdf`)
  }

  const handleStoreSsn = (userSsn: string, userType: 'mainUser' | 'partnerUser') => {
    if (userType === 'mainUser') {
      setUserMainSsn(userSsn)
      onSetMainUserSsn(userSsn)
    } else {
      setUserPartnerSsn(userSsn)
      onSetPartnerUserSsn(userSsn)
    }
  }

  return (
    <>
      <DownloadButton fullWidth direction="row" align="center" justify="space-between" top>
        <GrooverText textKey="registrationStep5PaymentReceiptHeader" />
        <ActionButtons align="center" gap={TWELVE_PX}>
          {!(userMainSsn || userPartnerSsn) ? (
            <Box
              direction="row"
              align="center"
              gap={EIGHT_PX}
              onClick={() => setShowPersonalNumberInput(!showPersonalNumberInput)}
            >
              <img src={plusIcon} width={16} height={16} />
              <GrooverText textKey="registrationCtactaAddPersonalNumber" />
            </Box>
          ) : (
            <Box
              direction="row"
              align="center"
              gap={EIGHT_PX}
              onClick={() => setShowPersonalNumberInput(!showPersonalNumberInput)}
            >
              <img src={penIcon} width={16} height={16} />
              <GrooverText textKey="registrationCtactaEditPersonalNumber" />
            </Box>
          )}
          <Box direction="row" align="center" gap={EIGHT_PX} onClick={() => handleDownloadPDF(0)}>
            <img src={printIcon} width={16} height={16} />
            <GrooverText textKey="registrationCtactaDownloadReceipt" />
          </Box>
        </ActionButtons>
      </DownloadButton>
      {client && showPersonalNumberInput && userType.length > 0 && (
        <>
          <CourseRegistrationReceiptSsn
            state={state}
            client={client}
            userType={userType[0]}
            onStoreSsn={handleStoreSsn}
          />
          {userType.length > 1 && (
            <CourseRegistrationReceiptSsn
              state={state}
              client={client}
              userType={userType[1]}
              onStoreSsn={handleStoreSsn}
            />
          )}
        </>
      )}
      <CourseRegistrationReceipt
        state={state}
        obfuscatedEnrollment={state.obfuscatedEnrollment.mainUser?.id ? state.obfuscatedEnrollment : enrollment}
        onHandleDownloadPDF={handleDownloadPDF}
      />
    </>
  )
}

const DownloadButton = styled(Box)`
  cursor: pointer;
`

const ActionButtons = styled(Box)`
  flex-direction: row;
  align-items: center;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

export default CourseRegistrationReceiptWrapper
